/**
 * CreatorCard Component
 * Displays a creator/influencer card with profile, stats, and actions
 * Used in Creator Discovery page
 */

import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Videocam as VideocamIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useDarkMode } from '../../hooks/useDarkMode';

export interface CreatorCardProps {
  creatorId: string;
  storeName: string;
  storeDescription: string;
  profileImage?: string;
  totalVideos?: number;
  totalFollowers?: number;
  avgEngagementRate?: number;
  onClick?: (creatorId: string) => void;
  onInvite?: (creatorId: string) => void;
}

export default function CreatorCard({
  creatorId,
  storeName,
  storeDescription,
  profileImage,
  totalVideos = 0,
  totalFollowers = 0,
  avgEngagementRate = 0,
  onClick,
  onInvite,
}: CreatorCardProps) {
  const isDarkMode = useDarkMode();

  const handleCardClick = () => {
    if (onClick) {
      onClick(creatorId);
    }
  };

  const handleInviteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onInvite) {
      onInvite(creatorId);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: isDarkMode ? '#1e1e1e' : '#fff',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        } : {},
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Profile Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={profileImage}
            alt={storeName}
            sx={{
              width: 60,
              height: 60,
              bgcolor: isDarkMode ? '#333' : '#e0e0e0',
            }}
          >
            {storeName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: isDarkMode ? '#fff' : '#000',
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {storeName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? '#aaa' : '#666',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {storeDescription}
            </Typography>
          </Box>
        </Box>

        {/* Stats Section */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Chip
            icon={<VideocamIcon sx={{ fontSize: 16 }} />}
            label={`${formatNumber(totalVideos)} 비디오`}
            size="small"
            sx={{
              bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#fff' : '#000',
            }}
          />
          <Chip
            icon={<PeopleIcon sx={{ fontSize: 16 }} />}
            label={`${formatNumber(totalFollowers)} 팔로워`}
            size="small"
            sx={{
              bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#fff' : '#000',
            }}
          />
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
            label={`${avgEngagementRate.toFixed(2)}% 참여율`}
            size="small"
            sx={{
              bgcolor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#fff' : '#000',
            }}
          />
        </Box>
      </CardContent>

      {/* Card Actions */}
      {onInvite && (
        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="contained"
            fullWidth
            onClick={handleInviteClick}
          >
            파트너 초대
          </Button>
        </CardActions>
      )}
    </Card>
  );
}
